import { IReduxAction } from './index'
import { AxiosError } from 'axios'
import { formatIsoDateTime } from '../helpers/dateHelper'
import { ReactElement } from 'react'
import { Destination, isGraphError, isSpringBootError } from '../context/AxiosContext'

export enum ErrorType {
    Network = 'NETWORK',
    Timeout = 'TIMEOUT',
    Outlook = 'OUTLOOK',
    Frontend = 'FRONTEND',
    Graph = 'GRAPH',
    Backend = 'BACKEND',
    Axios = 'AXIOS',
    Unknown = 'UNKNOWN',
}

export type NormalizedError = {
    id: string | ErrorType
    type: ErrorType
    code?: string
    message?: ReactElement | string
    timestamp?: string
    source?: string
    trace?: string
    exception?: Error
}

export type ErrorState = Readonly<Map<string, NormalizedError>>

export enum ErrorAction {
    Dismiss = 'DISMISS_ERROR',
    DismissNetworkError = 'DISMISS_NETWORK_ERROR',
    PushError = 'PUSH_ERROR',
}

export const dismissError = (id: string | ErrorType): IReduxAction<string> => ({
    type: ErrorAction.Dismiss,
    payload: id,
})

export const pushException = (normalizedError: NormalizedError, exception: Error): IReduxAction<NormalizedError> => ({
    type: ErrorAction.PushError,
    payload: { exception, timestamp: formatIsoDateTime(new Date()), ...normalizedError },
})

const getErrorType = (error: AxiosError): ErrorType => {
    // Pick out network errors from Hammock, to prevent multiple errors to be shown at the same time.
    if (error.code === 'ERR_NETWORK' && error.config.headers['Destination'] === Destination.Hammock) {
        return ErrorType.Network
    }

    if (['ECONNABORTED', 'ETIMEDOUT'].includes(error.code)) {
        return ErrorType.Timeout
    }

    return ErrorType.Axios
}

const getErrorMessage = (error: AxiosError): string => {
    switch (getErrorType(error)) {
        case ErrorType.Network:
            return 'De server is onbereikbaar'
        case ErrorType.Timeout:
            return `De server reageerde niet binnen een limiet van ${error.config.timeout / 1000} seconden`
        default:
            return error.message
    }
}

export const normalizeAxiosError = (error: AxiosError): NormalizedError => {
    if (isSpringBootError(error)) {
        // The Spring Boot backend has an error message.
        return {
            type: ErrorType.Backend,
            id: error.response.data.message,
            message: error.response.data.message,
            timestamp: error.response.data.timestamp,
            trace: error.response.data.trace ?? error.response.data.cause?.error?.message?.value,
        }
    }

    if (isGraphError(error)) {
        return {
            type: ErrorType.Graph,
            id: error.response.data.error.code,
            code: error.response.data.error.code,
            message: error.response.data.error.message,
            timestamp: error.response.data.error.innerError?.date,
        }
    }

    // Axios has an error message that is not a Network Error.
    return {
        id: error.code,
        code: error.code,
        type: getErrorType(error),
        message: getErrorMessage(error),
    }
}

export default function reducer(state: Readonly<ErrorState> = new Map(), action: IReduxAction): ErrorState {
    const _state = new Map(state as Map<string, NormalizedError>)
    switch (action.type) {
        case ErrorAction.PushError: {
            return _state.set(action.payload.id, { ...(action.payload as NormalizedError) })
        }
        case ErrorAction.DismissNetworkError: {
            _state.delete('ERR_NETWORK')
            break
        }
        case ErrorAction.Dismiss: {
            _state.delete(action.payload as string)
            break
        }
    }

    return _state
}
